/**
 * Strict validation mappings for vehicle fields when published to platforms
 * This ensures consistency across platforms by using common values in the UI
 * and mapping them to platform-specific values during API calls
 */

export interface BodyTypeMapping {
  enum: string // The clean Veekls ENUM (e.g., 'BODY.AUTOMOBILE.SUV')
  common: string // What users see in dropdown
  chileautos: string // Value sent to ChileAutos API
  mercadolibre: string // Value sent to MercadoLibre API
  yapo?: string // Value sent to Yapo API (not yet implemented)
}

/**
 * Body type mappings based on the common vehicle types table
 * Common column shows what users see in the dropdown
 * Platform columns show what gets sent to each platform
 */
export const BODY_TYPE_MAPPINGS: BodyTypeMapping[] = [
  {
    enum: 'BODY.AUTOMOBILE.HATCHBACK',
    common: 'Hatchback',
    chileautos: 'Hatchback',
    mercadolibre: 'Hatchback',
    yapo: 'Hatchback',
  },
  {
    enum: 'BODY.AUTOMOBILE.SUV',
    common: 'SUV',
    chileautos: 'SUV',
    mercadolibre: 'SUV',
    yapo: 'SUV',
  },
  {
    enum: 'BODY.AUTOMOBILE.SEDAN',
    common: 'Sedán / Sedan',
    chileautos: 'Sedán',
    mercadolibre: 'Sedán',
    yapo: 'Sedan',
  },
  {
    enum: 'BODY.AUTOMOBILE.VAN',
    common: 'Van',
    chileautos: 'Van',
    mercadolibre: 'Van',
    yapo: 'Van / Coaster / Busito',
  },
  {
    enum: 'BODY.AUTOMOBILE.PICK_UP',
    common: 'Pickup',
    chileautos: 'Pick-up',
    mercadolibre: 'Pick-Up',
    yapo: 'Pickup',
  },
  {
    enum: 'BODY.AUTOMOBILE.COUPE',
    common: 'Coupé',
    chileautos: 'Coupé',
    mercadolibre: 'Coupé',
    yapo: 'Coupe / Deportivo',
  },
]

/**
 * Get common body type values for dropdown display
 */
export const getCommonBodyTypes = (): { enum: string; common: string }[] => {
  return BODY_TYPE_MAPPINGS.map((mapping) => ({ enum: mapping.enum, common: mapping.common }))
}

/**
 * Convert common body type to platform-specific value
 * NOTE: For ChileAutos, the backend automatically handles this mapping in bodyTranslateEx()
 * This function is primarily for frontend display and MercadoLibre/Yapo integrations
 */
export const mapBodyTypeToPlatform = (
  commonValue: string,
  platform: 'chileautos' | 'mercadolibre' | 'yapo'
): string => {
  const mapping = BODY_TYPE_MAPPINGS.find((m) => m.common === commonValue || m.enum === commonValue)

  if (!mapping) {
    console.warn(`No mapping found for body type: ${commonValue}`)

    return commonValue // Fallback to original value
  }

  return mapping[platform] || commonValue
}

/**
 * Convert platform-specific body type back to common value (ENUM)
 */
export const mapBodyTypeFromPlatform = (
  platformValue: string,
  platform: 'chileautos' | 'mercadolibre' | 'yapo'
): string => {
  const mapping = BODY_TYPE_MAPPINGS.find((m) => m[platform] === platformValue)

  if (!mapping) {
    console.warn(`No reverse mapping found for ${platform} body type: ${platformValue}`)

    return platformValue // Fallback to original value
  }

  return mapping.enum
}

/**
 * Check if a body type value is a common value (used in dropdown)
 */
export const isCommonBodyType = (value: string): boolean => {
  return BODY_TYPE_MAPPINGS.some((mapping) => mapping.common === value || mapping.enum === value)
}

/**
 * Check if a body type value is a platform-specific value
 */
export const isPlatformBodyType = (value: string, platform: 'chileautos' | 'mercadolibre' | 'yapo'): boolean => {
  return BODY_TYPE_MAPPINGS.some((mapping) => mapping[platform] === value)
}

// ============== FUEL TYPE MAPPINGS ==============

export interface FuelTypeMapping {
  enum: string // The clean Veekls ENUM (e.g., 'FUEL.DIESEL')
  common: string // What users see in dropdown
  chileautos: string // Value sent to ChileAutos API
  mercadolibre: string // Value sent to MercadoLibre API
  yapo?: string // Value sent to Yapo API
}

/**
 * Fuel type mappings based on the common fuel types table
 * Common column shows what users see in the dropdown
 * Platform columns show what gets sent to each platform
 */
export const FUEL_TYPE_MAPPINGS: FuelTypeMapping[] = [
  {
    enum: 'FUEL.UNLEADED',
    common: 'Bencina',
    chileautos: 'Bencina',
    mercadolibre: 'Bencina',
    yapo: 'Bencina',
  },
  {
    enum: 'FUEL.DIESEL',
    common: 'Diésel',
    chileautos: 'Diesel',
    mercadolibre: 'Diésel',
    yapo: 'Diesel',
  },
  {
    enum: 'FUEL.ELECTRIC',
    common: 'Eléctrico',
    chileautos: 'Eléctrico',
    mercadolibre: 'Eléctrico',
    yapo: 'Eléctrico',
  },
  {
    enum: 'FUEL.NATURAL_GAS',
    common: 'Gas',
    chileautos: 'Gas',
    mercadolibre: 'Bencina/Gas',
    yapo: 'Gas',
  },
  {
    enum: 'FUEL.HYBRID',
    common: 'Híbrido',
    chileautos: 'Híbrido',
    mercadolibre: 'Híbrido',
    yapo: 'Híbrido',
  },
]

/**
 * Get common fuel type values for dropdown display
 */
export const getCommonFuelTypes = (): { enum: string; common: string }[] => {
  return FUEL_TYPE_MAPPINGS.map((mapping) => ({ enum: mapping.enum, common: mapping.common }))
}

/**
 * Map common fuel type value to platform-specific value
 */
export const mapFuelTypeToPlatform = (
  commonValue: string,
  platform: 'chileautos' | 'mercadolibre' | 'yapo'
): string => {
  const mapping = FUEL_TYPE_MAPPINGS.find((m) => m.common === commonValue || m.enum === commonValue)

  if (!mapping) return commonValue

  return mapping[platform] || commonValue
}

// ============== COLOR TYPE MAPPINGS ==============

export interface ColorTypeMapping {
  common: string // What users see in dropdown
  chileautos: string // Value sent to ChileAutos API
  mercadolibre: string // Value sent to MercadoLibre API
  yapo?: string // Value sent to Yapo API
}

/**
 * Color type mappings for external platform synchronization
 * Common column shows what users see in the dropdown
 * Platform columns show what gets sent to each platform
 */
export const COLOR_TYPE_MAPPINGS: ColorTypeMapping[] = [
  {
    common: 'Amarillo',
    chileautos: 'Amarillo',
    mercadolibre: 'Amarillo',
    yapo: 'Amarillo',
  },
  {
    common: 'Azul',
    chileautos: 'Azul',
    mercadolibre: 'Azul',
    yapo: 'Azul',
  },
  {
    common: 'Beige',
    chileautos: 'Beige',
    mercadolibre: 'Beige',
    yapo: 'Beige',
  },
  {
    common: 'Blanco',
    chileautos: 'Blanco',
    mercadolibre: 'Blanco',
    yapo: 'Blanco',
  },
  {
    common: 'Dorado',
    chileautos: 'Dorado',
    mercadolibre: 'Dorado',
    yapo: 'Dorado',
  },
  {
    common: 'Gris',
    chileautos: 'Gris',
    mercadolibre: 'Gris',
    yapo: 'Gris',
  },
  {
    common: 'Marrón',
    chileautos: 'Marrón',
    mercadolibre: 'Marrón',
    yapo: 'Marrón',
  },
  {
    common: 'Naranja',
    chileautos: 'Naranja',
    mercadolibre: 'Naranja',
    yapo: 'Naranja',
  },
  {
    common: 'Negro',
    chileautos: 'Negro',
    mercadolibre: 'Negro',
    yapo: 'Negro',
  },
  {
    common: 'Rojo',
    chileautos: 'Rojo',
    mercadolibre: 'Rojo',
    yapo: 'Rojo',
  },
  {
    common: 'Rosa',
    chileautos: 'Rosa',
    mercadolibre: 'Rosa',
    yapo: 'Rosa',
  },
  {
    common: 'Verde',
    chileautos: 'Verde',
    mercadolibre: 'Verde',
    yapo: 'Verde',
  },
  {
    common: 'Plata / Plateado',
    chileautos: 'Plata', // ChileAutos uses "Plata"
    mercadolibre: 'Plateado', // MercadoLibre uses "Plateado"
    yapo: 'Plata', // Yapo uses "Plata"
  },
]

/**
 * Get common color type values for dropdown display
 */
export const getCommonColorTypes = (): string[] => {
  return COLOR_TYPE_MAPPINGS.map((mapping) => mapping.common)
}

/**
 * Map common color type value to platform-specific value
 */
export const mapColorTypeToPlatform = (
  commonValue: string,
  platform: 'chileautos' | 'mercadolibre' | 'yapo'
): string => {
  const mapping = COLOR_TYPE_MAPPINGS.find((m) => m.common === commonValue)

  if (!mapping) {
    console.warn(`No mapping found for color type: ${commonValue}`)

    return commonValue // Fallback to original value
  }

  return mapping[platform] || commonValue
}

/**
 * Map platform-specific fuel type value to common value (ENUM)
 */
export const mapFuelTypeFromPlatform = (
  platformValue: string,
  platform: 'chileautos' | 'mercadolibre' | 'yapo'
): string => {
  const mapping = FUEL_TYPE_MAPPINGS.find((m) => m[platform] === platformValue)

  return mapping ? mapping.enum : platformValue
}

/**
 * Check if a fuel type value is a common value
 */
export const isCommonFuelType = (value: string): boolean => {
  return FUEL_TYPE_MAPPINGS.some((mapping) => mapping.common === value || mapping.enum === value)
}

/**
 * Check if a fuel type value is a platform-specific value
 */
export const isPlatformFuelType = (value: string, platform: 'chileautos' | 'mercadolibre' | 'yapo'): boolean => {
  return FUEL_TYPE_MAPPINGS.some((mapping) => mapping[platform] === value)
}

// ============== COLOR MAPPINGS ==============

/**
 * Standard color options for vehicle dropdowns
 * These colors are used across all platforms without mapping needed
 */
export const COLOR_OPTIONS: string[] = [
  'Amarillo',
  'Azul',
  'Beige',
  'Blanco',
  'Dorado',
  'Gris',
  'Marrón',
  'Naranja',
  'Negro',
  'Rojo',
  'Rosa',
  'Verde',
]

/**
 * Get color options for dropdown display
 */
export const getCommonColors = (): string[] => {
  return COLOR_OPTIONS
}

/**
 * Check if a color value is a standard color option
 */
export const isCommonColor = (value: string): boolean => {
  return COLOR_OPTIONS.includes(value)
}
